import React, { useEffect, useState } from 'react';
import { Form, Modal } from 'react-bootstrap';

import ValidationAlert from '../../../app/components/ValidationAlert';
import {
  ResponseError,
  UpdateAdminApplicationDto,
} from '../../../common/pokCore/autogenerated/pokApiClient';
import { useNotifications } from '../../../common/hooks/useNotifications';
import LastEditorInfo from '../../../app/components/LastEditorInfo';
import { usePokCore } from '../../../common/hooks/usePokCore';
import {
  ApplicationStatusEnum,
  ApplicationTypeEnum,
} from '../../../common/pokCore/validation/enums';
import momencik from '../../../common/momencik';
import { responseErrors } from '../../../common/pokCore/validation/responseErrors';
import Button from '../../../common/components/Button/Button';
import { useTranslation } from '../../../common/hooks/useTranslation';

import { ApplicationEditorFormFields } from './ApplicationEditorFormFields';
import { ApplicationAdminEditorStatus } from './ApplicationAdminEditorStatus';
import { EmailMessageText } from './EmailMessageText';

interface ApplicationEditorProps {
  id?: string;
  application: UpdateAdminApplicationDto;
  purchaserName?: string;
  positionName?: string;
  brandName?: string;
  author?: string;
  createdDate?: Date | null;
  handleClose?: () => void;
  show?: boolean;
  isReadOnly?: boolean;
  onCompleted: (
    client: UpdateAdminApplicationDto,
    id?: string,
  ) => Promise<{
    saved: boolean;
    errors: string[];
  }>;
}

export const ApplicationEditor: React.FC<ApplicationEditorProps> = props => {
  const [application, setApplication] = useState(props.application);
  const [showSaveErrors, setShowSaveErrors] = useState(false);
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const [formClassName, setFormClassName] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const pok = usePokCore();
  const notifications = useNotifications();
  const { t, tk } = useTranslation('applicationLng');

  const createApplication = async () => {
    setIsLoading(true);
    props
      .onCompleted(application, props.id)
      .then(saveStatus => {
        if (!saveStatus.saved) {
          setSaveErrors(saveStatus.errors);
          setShowSaveErrors(true);
          return;
        }
        notifications.saveCompleted();
      })
      .catch(async errorResponse => {
        notifications.caughtError(errorResponse);
      })
      .finally(() => setIsLoading(false));
  };

  const propertyChange = (obj: Partial<UpdateAdminApplicationDto>) => {
    setApplication({ ...application, ...obj });
  };

  const handleClose = () => {
    setApplication({ ...application, ...{ countryIsMain: undefined } });
    return props.handleClose && props.handleClose();
  };

  useEffect(() => {
    if (props.show) {
      setSaveErrors([]);
      setShowSaveErrors(false);
      setApplication(props.application);
    }
  }, [props.application, props.show]);

  useEffect(() => {
    const show =
      application.applicationType === ApplicationTypeEnum.Permission ||
      application.applicationType === ApplicationTypeEnum.Position ||
      application.applicationType === ApplicationTypeEnum.Purchaser;
    setFormClassName(show ? 'd-flex flex-wrap gap-1 mb-3' : '');
  }, [application.applicationType]);

  const findByNip = async (nip?: string) => {
    if (!nip) {
      setSaveErrors([t(tk.app.enterNIP)]);
      setShowSaveErrors(true);
      return;
    }
    try {
      const nipClient = await pok.clients.getByNip(nip.trim());
      if (nipClient) {
        setShowSaveErrors(false);
        setApplication({
          ...application,
          name: nipClient.name || '',
          postcode: nipClient.postcode || '',
          place: nipClient.place || '',
          street: nipClient.street || '',
          numbers: nipClient.numbers || '',
          flat: nipClient.flat || '',
        });
      }
    } catch (response) {
      const errors = await responseErrors(response as ResponseError);
      setSaveErrors(errors);
      setShowSaveErrors(true);
    }
  };

  return (
    <>
      <Modal
        onHide={handleClose}
        show={props.show}
        centered={true}
        keyboard={true}
        backdrop="static"
        size="xl"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {props.id
              ? t(tk.app.editingApplication, {
                  createdDate: momencik(props.createdDate),
                })
              : t(tk.app.addingNewApplication)}
            {props.id && (
              <small>
                {' '}
                ({t(tk.app.creator)}: {props.author})
              </small>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form className={formClassName}>
            <ApplicationEditorFormFields
              application={application}
              isReadOnly={props.isReadOnly}
              propertyChange={propertyChange}
              findByNip={findByNip}
              show={props.show}
            />
            {props.application.applicationStatus &&
              props.application.applicationStatus !==
                ApplicationStatusEnum.New && (
                <ApplicationAdminEditorStatus
                  application={application}
                  purchaserName={props.purchaserName}
                  positionName={props.positionName}
                  brandName={props.brandName}
                  isReadOnly={true}
                />
              )}
            {application.applicationType === ApplicationTypeEnum.Position &&
              application.countryIsMain === false && <EmailMessageText />}
          </Form>
          <ValidationAlert
            show={showSaveErrors}
            errors={saveErrors}
            className="m-3"
          />
        </Modal.Body>
        <Modal.Footer>
          {props.id && (
            <LastEditorInfo
              id={props.id}
              method={pok.applications.getLastEditor}
            />
          )}
          <Button variant="outline-secondary" onClick={handleClose}>
            {t(tk.button.close)}
          </Button>
          {/* {application.applicationType !== ApplicationTypeEnum.Position && ( */}
          <Button
            variant="primary"
            onClick={createApplication}
            disabled={props.isReadOnly}
            isLoading={isLoading}
          >
            {t(tk.button.save)}
          </Button>
          {/* )} */}
        </Modal.Footer>
      </Modal>
    </>
  );
};
